
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import PieChartFilterable from '@/components/reports-v3/components/filterables/PieChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import { GobiColor } from '@/helpers/color';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import moment from 'moment';
import { TooltipFormatter } from '../../elements/charts/helpers/tooltipFormatter';
import gdbx from '@/store/modules/gdbx';
import { roundToFixed } from '@/util/number';

@Component({
	components: {
		PieChartFilterable,
	},
})
export default class ProjectBillingPieChart extends FilterWidget {
	@Prop({ default: () => '' }) public readonly selectedProjectCode!: string;

	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public pieDatas: Array<{
		name: string;
		value: number;
		itemStyle: { color: any };
	}> = [];

	public get currentYearRange(): [number, number] {
		return [
			moment(this.selectedAsOfDate).startOf('year').valueOf(),
			moment(this.selectedAsOfDate).startOf('month').valueOf(),
		];
	}

	public get dataViewFormatter() {
		return DataViewFormatter.pieChart;
	}

	public get tooltipFormatter() {
		return TooltipFormatter.pieChart;
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get expensiveHook() {
		const { selectedProjectCode, selectedAsOfDate } = this;
		return JSON.stringify([selectedProjectCode, selectedAsOfDate]);
	}

	public async expensiveCalc() {
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const cfTable = getTableName('projectCashFlow');
		const projectCode = escapeStr(this.selectedProjectCode);

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(
			['projectCashFlow'],
		);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		const ytdYear = moment(this.selectedAsOfDate).year();
		const currentMonth =
			moment(this.selectedAsOfDate).startOf('month').month() + 1;

		const result = await queryHead.rawQuery(`
			SELECT
				SUM(TRY_CAST("CUSTOMERPAYMENT" AS DOUBLE)
					+ TRY_CAST("CUSTOMERCONTRA" AS DOUBLE)
					+ TRY_CAST("CUSTOMERREFUND" AS DOUBLE)) as collection,
				SUM(TRY_CAST("CUSTOMERINVOICE" AS DOUBLE)
					+ TRY_CAST("CUSTOMERDEBIT" AS DOUBLE)
					+ TRY_CAST("CUSTOMERCREDIT" AS DOUBLE)) as billing
			FROM ${cfTable}
			WHERE "PROJECT" = '${projectCode}'
				AND (TRY_CAST("YEAR" AS INT) < ${ytdYear}
					OR (TRY_CAST("YEAR" AS INT) = ${ytdYear}
						AND TRY_CAST("MONTH" AS INT) <= ${currentMonth}))
		`);
		this.checkInterrupted(jobId);

		const collection = result[0] && Number(result[0].collection) || 0;
		const billing = result[0] && Number(result[0].billing) || 0;

		this.pieDatas = [
			{
				name: 'Collection',
				value: roundToFixed(collection, this.decimal),
				itemStyle: { color: GobiColor.TEAL },
			},
			{
				name: 'Pending',
				value: roundToFixed(billing - collection, this.decimal),
				itemStyle: { color: GobiColor.ORANGE },
			},
		];

		this.generateLoadingText(1);

		this.saveHistory('pieDatas');
	}
}
